import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Handshake, ArrowsMerge, Scales, CaretDown, CaretRight, LockKey, LockKeyOpen,
  ArrowsClockwise, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (v) => (v == null ? "—" : formatCurrency(v));

const SECTIONS = [
  { v: "commitments", l: "Commitments Register", i: Handshake },
  { v: "migration", l: "Legacy Migration", i: ArrowsMerge },
  { v: "reconciliation", l: "Reconciliation & Lock", i: Scales },
];

export default function ConsolidationTab({ canEdit, isSuperAdmin }) {
  const [section, setSection] = useState("commitments");
  return (
    <div className="space-y-5" data-testid="consolidation-tab">
      <div className="flex flex-wrap gap-1.5">
        {SECTIONS.map((s) => (
          <button key={s.v} onClick={() => setSection(s.v)} data-testid={`consol-pill-${s.v}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              section === s.v ? "border-[#0f1e42] bg-[#0f1e42] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            <s.i size={13} /> {s.l}
          </button>
        ))}
      </div>
      {section === "commitments" ? <CommitmentsSection canEdit={canEdit} />
        : section === "migration" ? <MigrationSection canEdit={canEdit} />
        : <ReconSection canEdit={canEdit} isSuperAdmin={isSuperAdmin} />}
    </div>
  );
}

const STATUS_TONE = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  partially_drawn: "bg-amber-50 text-amber-700 border-amber-200",
  fully_drawn: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
  superseded: "bg-violet-50 text-violet-500 border-violet-200",
};

function CommitmentsSection({ canEdit }) {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [closing, setClosing] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, l] = await Promise.all([
      api.get("/finance/commitments/summary"),
      api.get("/finance/commitments", { params: { status: status === "all" ? "" : status, q } }),
    ]);
    setSummary(s.data); setRows(l.data.items || []);
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [status]); // eslint-disable-line

  const backfill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/commitments/backfill");
      toast.success(`Register built — ${data.created_procurement} PO + ${data.created_subcontract} subcontract commitment(s), ${data.drawdowns_replayed} drawdown(s) replayed`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doClose = async () => {
    try {
      await api.post(`/finance/commitments/${closing.id}/close`, { reason });
      toast.success(`${closing.commitment_no} closed`);
      setClosing(null); setReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { l: "Active commitments", v: summary?.count, money: false, tid: "commit-kpi-count" },
          { l: "Committed", v: summary?.total_committed, money: true, tid: "commit-kpi-committed" },
          { l: "Drawn down", v: summary?.total_drawn, money: true, tid: "commit-kpi-drawn" },
          { l: "Remaining", v: summary?.total_remaining, money: true, tid: "commit-kpi-remaining" },
        ].map((k) => (
          <div key={k.l} className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={k.tid}>
            <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{k.l}</div>
            <div className="mt-1 text-lg font-semibold tabular-nums text-slate-900">
              {k.v == null ? "—" : k.money ? fmt(k.v) : k.v}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search title, vendor, ref…" value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && load()} className="h-8 w-56 text-xs" data-testid="commit-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="commit-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="partially_drawn">Partially drawn</SelectItem>
            <SelectItem value="fully_drawn">Fully drawn</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="superseded">Superseded</SelectItem>
          </SelectContent>
        </Select>
        {canEdit && (
          <Button size="sm" variant="outline" className="ml-auto h-8 text-xs" onClick={backfill} disabled={busy}
                  data-testid="commit-backfill-btn">
            <ArrowsClockwise size={13} className="mr-1" /> {busy ? "Building…" : "Build / refresh register"}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2.5">No</th><th className="px-4 py-2.5">Source</th>
              <th className="px-4 py-2.5">Title / Vendor</th><th className="px-4 py-2.5">Project</th>
              <th className="px-4 py-2.5 text-right">Committed</th><th className="px-4 py-2.5 text-right">Drawn</th>
              <th className="px-4 py-2.5 text-right">Remaining</th><th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-xs text-slate-400" data-testid="commit-empty">
                No commitments yet — award a PO / create a subcontract, or build the register from existing records.
              </td></tr>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    data-testid={`commit-row-${r.commitment_no}`}>
                  <td className="px-4 py-2.5 font-mono-alt text-xs">{r.commitment_no}</td>
                  <td className="px-4 py-2.5">
                    <span className="font-mono-alt text-[10px] uppercase text-slate-500">{r.source_type === "subcontract" ? "Subcontract" : "PO"}</span>
                    <span className="ml-1.5 font-mono-alt text-[10px] text-slate-400">{r.source_ref}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{r.title}</div>
                    <div className="text-[11px] text-slate-500">{r.vendor_name || "—"}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{r.project_name || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-alt tabular-nums">{fmt(r.revised_amount)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-alt tabular-nums text-amber-700">{fmt(r.drawn_amount)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-alt tabular-nums font-semibold">{fmt(r.remaining)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[r.status] || STATUS_TONE.open}`}>
                      {r.status?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {openId === r.id ? <CaretDown size={12} className="inline text-slate-400" /> : <CaretRight size={12} className="inline text-slate-400" />}
                  </td>
                </tr>
                {openId === r.id && (
                  <tr className="bg-slate-50/60">
                    <td colSpan={9} className="px-6 py-3" data-testid={`commit-drawdowns-${r.commitment_no}`}>
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                          Drawdown ledger ({(r.drawdowns || []).length})
                        </span>
                        {canEdit && r.status !== "closed" && r.status !== "superseded" && (
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]"
                                  onClick={(e) => { e.stopPropagation(); setClosing(r); }}
                                  data-testid={`commit-close-${r.commitment_no}`}>Close commitment</Button>
                        )}
                      </div>
                      {(r.drawdowns || []).length === 0 ? (
                        <div className="text-[11px] text-slate-400">No drawdowns — no AP bills processed against this commitment yet.</div>
                      ) : (
                        <ul className="space-y-1">
                          {r.drawdowns.map((d) => (
                            <li key={d.id} className="flex items-center gap-3 text-xs">
                              <span className="font-mono-alt text-[10px] text-slate-400">{formatDate(d.at)}</span>
                              <span className="font-mono-alt text-[10px]">{d.bill_number || d.ap_bill_id?.slice(0, 8)}</span>
                              <span className="flex-1 truncate text-slate-500">{d.note}</span>
                              <span className="font-mono-alt tabular-nums text-amber-700">−{fmt(d.amount)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {r.status === "closed" && (
                        <div className="mt-1.5 text-[11px] text-slate-500">Closed by {r.closed_by} · {r.close_reason}</div>
                      )}
                      {r.status === "superseded" && (
                        <div className="mt-1.5 text-[11px] text-violet-600">Superseded by a subcontract commitment (no double counting).</div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!closing} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Close {closing?.commitment_no}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Remaining {fmt(closing?.remaining)} will be released. This is recorded in the audit trail.
          </p>
          <div>
            <Label>Reason (required)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. PO completed under budget"
                   data-testid="commit-close-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosing(null)}>Cancel</Button>
            <Button onClick={doClose} disabled={!reason.trim()} data-testid="commit-close-confirm">Close commitment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MigrationSection({ canEdit }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/finance/consolidation/status");
    setStatus(data);
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []);

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "entries") {
        const { data } = await api.post("/finance/consolidation/migrate-entries");
        toast.success(`${data.posted_count} entr(ies) posted to GL · ${data.skipped_count} skipped`);
      } else {
        const { data } = await api.post("/finance/consolidation/migrate-invoices");
        toast.success(`${data.merged_count} invoice(s) merged to AR · ${data.failed_count} failed`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const fe = status?.finance_entries;
  const inv = status?.legacy_invoices;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="migration-entries-card">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Legacy ledger entries → GL</h3>
            {fe && (fe.journalable_unposted === 0
              ? <CheckCircle size={16} className="text-emerald-600" />
              : <WarningCircle size={16} className="text-amber-600" />)}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Flat ledger entries are mirrored as double-entry GL journals. Idempotent — nothing is posted twice.
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Stat l="Total" v={fe?.total} />
            <Stat l="Posted" v={fe?.posted} tone="text-emerald-700" />
            <Stat l="Unposted" v={fe?.journalable_unposted} tone={fe?.journalable_unposted ? "text-amber-700" : "text-slate-400"} tid="mig-fe-unposted" />
          </div>
          {fe && Object.keys(fe.unposted_reasons || {}).length > 0 && (
            <div className="mt-2 space-y-0.5">
              {Object.entries(fe.unposted_reasons).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[11px] text-slate-500">
                  <span>{k.replaceAll("_", " ")}</span><span className="font-mono-alt">{v}</span>
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <Button size="sm" className="mt-3 w-full" onClick={() => run("entries")}
                    disabled={busy !== "" || fe?.journalable_unposted === 0} data-testid="migrate-entries-btn">
              {busy === "entries" ? "Posting…" : "Post unposted entries to GL"}
            </Button>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="migration-invoices-card">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Legacy invoices → AR subledger</h3>
            {inv && (inv.unmerged === 0
              ? <CheckCircle size={16} className="text-emerald-600" />
              : <WarningCircle size={16} className="text-amber-600" />)}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Every legacy customer invoice becomes a proper AR invoice (with GL posting and receipts tracking).
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Stat l="Total" v={inv?.total} />
            <Stat l="Merged" v={inv?.merged_to_ar} tone="text-emerald-700" />
            <Stat l="Unmerged" v={inv?.unmerged} tone={inv?.unmerged ? "text-amber-700" : "text-slate-400"} tid="mig-inv-unmerged" />
          </div>
          {canEdit && (
            <Button size="sm" className="mt-3 w-full" onClick={() => run("invoices")}
                    disabled={busy !== "" || inv?.unmerged === 0} data-testid="migrate-invoices-btn">
              {busy === "invoices" ? "Merging…" : "Merge legacy invoices to AR"}
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-2.5 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
          Recent migration runs
        </div>
        {(status?.recent_runs || []).length === 0 ? (
          <div className="px-4 py-5 text-center text-xs text-slate-400">No migration runs yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {status.recent_runs.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 w-32">{r.kind?.replaceAll("_", " ")}</span>
                <span className="flex-1 text-slate-600">{r.posted} processed · {r.skipped} skipped</span>
                <span className="text-slate-500">{r.by}</span>
                <span className="font-mono-alt text-[10px] text-slate-400">{formatDate(r.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const Stat = ({ l, v, tone = "text-slate-900", tid }) => (
  <div className="rounded-md bg-slate-50 px-2 py-1.5" data-testid={tid}>
    <div className="font-mono-alt text-[9px] uppercase tracking-widest text-slate-400">{l}</div>
    <div className={`text-sm font-semibold tabular-nums ${tone}`}>{v == null ? "—" : v}</div>
  </div>
);

function ReconSection({ canEdit, isSuperAdmin }) {
  const [recon, setRecon] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forceReason, setForceReason] = useState("");

  const load = async () => {
    const [r, s] = await Promise.all([
      api.get("/finance/consolidation/reconciliation"),
      api.get("/finance/consolidation/status"),
    ]);
    setRecon(r.data); setStatus(s.data);
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []);

  const lock = async (force = false) => {
    setBusy(true);
    try {
      await api.post("/finance/consolidation/lock-legacy", { confirm: true, force, reason: forceReason });
      toast.success("Legacy ledger locked — read-only from now on");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const unlock = async () => {
    setBusy(true);
    try {
      await api.post("/finance/consolidation/unlock-legacy");
      toast.success("Legacy ledger unlocked");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2.5">Reconciliation</th>
              <th className="px-4 py-2.5 text-right">Legacy value</th>
              <th className="px-4 py-2.5 text-right">Modern value</th>
              <th className="px-4 py-2.5 text-right">Delta</th>
              <th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(recon?.rows || []).map((r) => (
              <tr key={r.key} data-testid={`recon-row-${r.key}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{r.label}</div>
                  <div className="text-[11px] text-slate-500">{r.note}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono-alt tabular-nums">{fmt(r.legacy_value)}</td>
                <td className="px-4 py-3 text-right font-mono-alt tabular-nums">{fmt(r.modern_value)}</td>
                <td className={`px-4 py-3 text-right font-mono-alt tabular-nums ${Math.abs(r.delta) > 0.01 ? "text-amber-700" : "text-slate-400"}`}>
                  {fmt(r.delta)}
                </td>
                <td className="px-4 py-3">
                  {r.matched
                    ? <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"><CheckCircle size={11} /> matched</span>
                    : <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"><WarningCircle size={11} /> delta</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(recon?.orphan_journals || []).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="orphan-journals-panel">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-amber-900">
                {recon.orphan_journals.length} orphan GL journal(s) · {fmt(recon.orphan_value)}
              </h3>
              <p className="mt-0.5 text-xs text-amber-700">
                Their legacy source entries were removed outside the app. Repair posts proper auditable reversals — nothing is deleted.
              </p>
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100"
                      onClick={repair} disabled={busy} data-testid="repair-orphans-btn">
                {busy ? "Repairing…" : "Repair orphan journals"}
              </Button>
            )}
          </div>
          <ul className="mt-2 space-y-0.5">
            {recon.orphan_journals.slice(0, 5).map((o) => (
              <li key={o.entry_no} className="flex items-center gap-3 text-[11px] text-amber-800">
                <span className="font-mono-alt">{o.entry_no}</span>
                <span className="flex-1 truncate">{o.memo}</span>
                <span className="font-mono-alt tabular-nums">{fmt(o.value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={`rounded-lg border p-4 ${status?.legacy_readonly ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white"}`}
           data-testid="legacy-lock-panel">
        <div className="flex items-start gap-3">
          {status?.legacy_readonly ? <LockKey size={20} className="mt-0.5 text-emerald-700" /> : <LockKeyOpen size={20} className="mt-0.5 text-slate-500" />}
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900">
              Legacy ledger is {status?.legacy_readonly ? "READ-ONLY" : "still writable"}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {status?.legacy_readonly
                ? `Locked by ${status?.lock_meta?.by || "—"} on ${status?.lock_meta?.at ? formatDate(status.lock_meta.at) : "—"}${status?.lock_meta?.reason ? ` · ${status.lock_meta.reason}` : ""}. New transactions must go through GL / AR / AP.`
                : recon?.ready_to_lock
                  ? "Reconciliation is clean — you can lock the legacy flat ledger so all new activity flows through the modern GL / AR / AP."
                  : `Not ready: ${recon?.unposted_journalable ?? "—"} unposted entr(ies), ${recon?.unmerged_invoices ?? "—"} unmerged invoice(s). Run the migrations first (or force-lock with a documented reason).`}
            </p>
            {canEdit && !status?.legacy_readonly && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => lock(false)} disabled={busy || !recon?.ready_to_lock} data-testid="lock-legacy-btn">
                  <LockKey size={13} className="mr-1" /> Lock legacy ledger
                </Button>
                {!recon?.ready_to_lock && (
                  <>
                    <Input value={forceReason} onChange={(e) => setForceReason(e.target.value)}
                           placeholder="Force-lock reason…" className="h-8 w-56 text-xs" data-testid="force-lock-reason" />
                    <Button size="sm" variant="outline" onClick={() => lock(true)} disabled={busy || !forceReason.trim()}
                            data-testid="force-lock-btn">Force lock</Button>
                  </>
                )}
              </div>
            )}
            {isSuperAdmin && status?.legacy_readonly && (
              <Button size="sm" variant="outline" className="mt-3" onClick={unlock} disabled={busy} data-testid="unlock-legacy-btn">
                <LockKeyOpen size={13} className="mr-1" /> Unlock (Super Admin)
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
